import { format } from 'date-fns';
import type React from 'react';
import { Link } from 'react-router';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import CollectedDataGrid from './CollectedDataGrid';
import ThirdPartyServicesGrid from './ThirdPartyServicesGrid';

interface PrivacyPolicyProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ ssrFetchedData }) => {
  const { toLocalizedRoute } = useAppNavigation();
  const lastPolicyUpdate = new Date('2025-04-04');

  const companyName = ssrFetchedData.appConfiguration?.company.owner;
  const serviceName = ssrFetchedData.appConfiguration?.service.name;
  const serviceWebsite = ssrFetchedData.domain;
  const companyLegalEntityName =
    ssrFetchedData.appConfiguration?.company.legacy_entity_name;
  const companyLegalEntityAddress =
    ssrFetchedData.appConfiguration?.company.legacy_entity_address;
  const companyLegalEntityVATNumber =
    ssrFetchedData.appConfiguration?.company.legacy_entity_vat_number;
  const companyLegalEntityCF =
    ssrFetchedData.appConfiguration?.company.legacy_entity_cf;

  const mainLanguageDisclaimer = () => (
    <section className="mb-8 font-thin">
      <div className="mt-4 italic">
        You’re reading the English version of the privacy policy. Among the
        various translated versions, the text of the Italian version will always
        prevail over the other versions in case of different interpretations
        and/or discrepancies.
      </div>
    </section>
  );

  return (
    <div className="flex flex-col items-center p-4">
      <div className="mx-2 max-w-7xl md:mx-8">
        <div className="mx-2 flex max-w-3xl flex-col items-start md:mx-8">
          <section>
            <div className="flex shrink-0 flex-col justify-between">
              <h1 className="mb-2 text-2xl font-black">Privacy Policy</h1>
              <h2 className="mb-2 text-sm font-semibold">
                Last updated: {format(lastPolicyUpdate, 'do MMMM yyyy')}
              </h2>
              {false && mainLanguageDisclaimer()}
            </div>
            <div className="my-8 font-normal">
              <div className="mb-6 text-xl font-bold">
                Brief summary for if you’re short on time
              </div>
              <section className="mt-10">
                <div className="mb-4 text-lg font-bold">
                  I. For User, Registered User or Private Advertiser that uses
                  our Services
                </div>
                <section className="mb-3">
                  {companyLegalEntityName}, with registered office at{' '}
                  {companyLegalEntityAddress}, VAT and tax code no.{' '}
                  {companyLegalEntityVATNumber}, C.F. {companyLegalEntityCF},
                  (hereinafter,{' '}
                  <span className="font-bold">
                    &quot;
                    {companyName}&quot;
                  </span>
                  ) is the data controller for processing the personal data you
                  provide us with via the Website, its relative subdomains or
                  the Apps, in order to provide to you our services.
                </section>
                <section className="mb-3">
                  At the time you request a service, you register in our
                  Services, this becomes a contractual relationship that is the
                  legal basis to be able to process your personal data.
                </section>
                <section className="mb-3">
                  We may also send you commercial messages when you have
                  accepted them.
                </section>
                <section className="mb-3">
                  If you have authorised the assignment of your data or you have
                  requested us for a service rendered by other companies, the
                  latter may need to have access to your data in order to be
                  able to render the service.
                </section>
                <section className="mb-3">
                  You can, at any time, access, rectify or cancel your data, as
                  well as exercise other rights, as explained below.
                </section>
              </section>
              <section className="mt-10">
                <div className="mb-4 text-lg font-bold">
                  II. For Professional Advertiser or company or representative
                  thereof that uses our Services for Business Profiles
                </div>
                <section className="mb-3">
                  {companyName} is the data controller for processing the
                  personal data you provide. We will process them in order to be
                  able to render the services you request based on the agreement
                  signed with the company you represent, this being the legal
                  basis to be able to process them in the legitimate interest of{' '}
                  {companyName}.
                </section>
                <section className="mb-3">
                  Your data may be shared with {companyName}&apos;s service
                  providers that must access them in order to render the
                  service.
                </section>
                <section className="mb-3">
                  You can, at any time, access, rectify or cancel your data, as
                  well as exercise other rights, as explained below.
                </section>
              </section>
              <section className="mt-10">
                <div className="mb-4 text-lg font-bold">You should know:</div>
                <section className="mb-3">
                  We always recommend that you fully read the privacy policy
                  that we provide below to be able to know in-depth which data
                  we use from each service you request and for how long we need
                  to keep them.
                </section>
                <section className="mb-3">
                  This privacy policy may be modified by a unilateral decision
                  by {companyName}, so we recommend that you review it
                  regularly.
                </section>
                <section className="mb-3">
                  When you click the &quot;Ok, let’s go&quot; button or
                  equivalent, you state you know and understand {companyName}
                  &apos;s privacy policy.
                </section>
                <section className="mb-3">
                  Now we get down to the nitty-gritty of the legal text, so that
                  you can have the peace of mind to know everything we do (and
                  don’t do) with your data. Go pop the kettle on and put on your
                  favourite song to make the reading just a little lighter.
                </section>
              </section>
              <hr className="my-8 h-px border-0 bg-gray-200 dark:bg-gray-700" />
              <section className="mt-10">
                <div className="mb-4 text-lg font-bold">Index</div>
                <ol className="list-decimal">
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section01`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Data controller: who is the data controller for processing
                      your data?
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section02`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Purpose of the processing: what can we do with your data?
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section03`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Legal basis: why can we process them?
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section04`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Recipients: to whom can we disclose your data?
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section05`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      International data transfers: where can we send them?
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section06`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Period for storing your data: how long will we process
                      them?
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section07`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Required updated information
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section08`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Rights of data subjects
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section09`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Confidentiality and security
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section10`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Additional information about certain functions of the
                      Services
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section11`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Types of data collected
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.PRIVACY_POLICY_ROUTE}#section12`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Third party services involved in the processing of your
                      data
                    </Link>
                  </li>
                </ol>
              </section>
              <section className="mt-16" id="section01">
                <div className="mb-4 text-xl font-bold">
                  1. Data controller: who is the data controller for processing
                  your data?
                </div>
                <section className="mb-3">
                  {companyLegalEntityName}, with registered office at{' '}
                  {companyLegalEntityAddress}, VAT and tax code no.{' '}
                  {companyLegalEntityVATNumber}, C.F. {companyLegalEntityCF},
                  (hereinafter,{' '}
                  <span className="font-bold">
                    &quot;
                    {companyName}&quot;
                  </span>
                  ) is the data controller for the processing of personal data
                  sent through our website and its subdomains or our mobile
                  applications or our tools (hereinafter, the{' '}
                  <span className="font-bold">&quot;Services&quot;</span>
                  ).
                </section>
                <section className="mb-3">
                  We provide information in the following sections about the
                  rights you (hereinafter referred to as the{' '}
                  <span className="font-bold">&quot;User&quot;</span> or{' '}
                  <span className="font-bold">&quot;Registered User&quot;</span>
                  ) hold as well as how we use your personal data, whether you
                  use our Services to search for information about professionals
                  or businesses advertised (hereinafter referred to as the{' '}
                  <span className="font-bold">
                    &quot;Business Profiles&quot;
                  </span>
                  ) or if you post a professional or business profile as an
                  individual (hereinafter referred to as the{' '}
                  <span className="font-bold">
                    &quot;Private Advertiser&quot;
                  </span>{' '}
                  or <span className="font-bold">&quot;Advertiser&quot;</span>)
                  or as a representative of a Professional Advertiser
                  (hereinafter referred to as the{' '}
                  <span className="font-bold">
                    &quot;Professional Advertiser&quot;
                  </span>{' '}
                  or <span className="font-bold">&quot;Advertiser&quot;</span>)
                  by virtue of the EU General Data Protection Regulations
                  2016/679 (hereinafter referred to as the{' '}
                  <span className="font-bold">&quot;GDPR&quot;</span>).
                </section>
                <section className="mb-3">
                  If you have any queries related to the processing of your
                  personal data, you can contact us at the following email
                  address:{' '}
                  <span className="font-semibold italic">
                    privacy(at){serviceWebsite}
                  </span>
                </section>
              </section>
              <section className="mt-16" id="section02">
                <div className="mb-4 text-xl font-bold">
                  2. Purpose of the processing: what can we do with your data?
                </div>
                <section className="mb-3">
                  <ol type="a" className="list-[upper-roman]">
                    <li className="mb-4">
                      We manage your personal data to render the services you
                      have requested through the Services. The list of services
                      includes the following:
                      <ol className="ml-8 list-disc">
                        <li>Signing-up as a user</li>
                        <li>
                          Saving and managing your searches for Business
                          Profiles
                        </li>
                        <li>Contacting advertisers</li>
                        <li>
                          Publication of listings on the website{' '}
                          {serviceWebsite} and/or on the websites of other
                          companies belonging to the {companyName} group,
                          branding and extra visibility.
                        </li>
                        <li>Posting advertisements</li>
                        <li>Contact chat</li>
                        <li>Business Profiles valuation service</li>
                        <li>
                          Multimedia services that help you to improve your
                          listing (including photo book, professional video,
                          text and image enhancements, etc.)
                        </li>
                        <li>
                          Sending information about Business Profiles that may
                          interest you.
                        </li>
                      </ol>
                      <br />
                      Please note that, if you have requested any of the above
                      services, we may contact you by phone, SMS or email (if
                      you have provided any of these data), for the provision of
                      the same.
                    </li>
                    <li className="mb-4">
                      In the case of a claim or query, we will process your data
                      in order to manage them.
                    </li>
                    <li className="mb-4">
                      If you are a User or a Private Advertiser, we may send you
                      commercial messages, if you have accepted them, for the
                      following purposes:
                      <ol className="ml-8 list-disc">
                        <li>
                          To send you information about Business Profiles, news
                          and other promotional messages related to{' '}
                          {companyName} or its partners
                        </li>
                        <li>
                          To send you special offers of companies that will help
                          you in the sale process of your Business Profiles
                          related services
                        </li>
                        <li>
                          To invite you to participate in satisfaction surveys
                        </li>
                      </ol>
                    </li>
                    <li className="mb-4">
                      If you are a User or Registered User, a profile may be
                      created that will include information about the searches
                      you have launched, the Business Profiles you have saved as
                      favourites or the contacts you have made with advertisers
                      for the purpose of showing you similar Business Profiles
                      through messages that could be of interest to you.
                    </li>
                    <li className="mb-4">
                      If you are a representative or contact person of a
                      Professional Advertiser, we manage your personal data to
                      keep in contact and develop the business relationship with
                      the companies you represent. Moreover, we may send you
                      messages for the following purposes:
                      <ol className="ml-8 list-disc">
                        <li>
                          To invite you to events and workshops held by{' '}
                          {companyName} or by its partners
                        </li>
                        <li>
                          To send you information about Business Profiles, news
                          and other promotional messages related to{' '}
                          {companyName} or by its partners
                        </li>
                        <li>
                          To invite you to participate in satisfaction surveys
                        </li>
                      </ol>
                    </li>
                    <li className="mb-4">
                      If you have purchased a paid service, are a Private
                      Advertiser or a representative or contact person of a
                      Professional Advertiser, we process your personal data to
                      manage any claims for payment for our services.
                    </li>
                  </ol>
                </section>
              </section>
              <section className="mt-16" id="section03">
                <div className="mb-4 text-xl font-bold">
                  3. Legal basis: why can we process them?
                </div>
                <section className="mb-3">
                  The legal basis that allows us to process your data for each
                  of the purposes specified in section 2 above are:
                  <ol type="a" className="mt-4 list-[upper-roman]">
                    <li className="mb-4">
                      <span className="font-bold">
                        Contractual relationship
                      </span>
                      : applicable when you request one of our services
                      specified in paragraph 2.I above, even if such services
                      are free of charge.
                    </li>
                    <li className="mb-4">
                      <span className="font-bold">Legitimate interest</span>: to
                      handle the queries and claims you send us (paragraph 2.II)
                      and manage claims for payments of due amounts (paragraph
                      2.VI). If you are a representative or contact person of a
                      Professional Advertiser, we have a legitimate interest to
                      remain in contact with the company to which we render
                      services and that you represent, also to invite you to our
                      events and workshops (paragraph 2.V)
                    </li>
                    <li className="mb-4">
                      <span className="font-bold">Your consent</span>:
                      <ol className="ml-8 list-disc">
                        <li>
                          If you are a Registered User or a Private Advertiser,
                          by checking the box included in the sign-up form or in
                          the user profile menu, you have authorised us to send
                          you the messages referred to in section 2.III above
                        </li>
                        <li>
                          If you are a representative or contact person of a
                          Professional Advertiser, by checking the box included
                          in the contact form or when contracting any of our
                          services, you have authorised us to send you the
                          messages referred to in section 2.V above
                        </li>
                      </ol>
                    </li>
                  </ol>
                </section>
              </section>
              <section className="mt-16" id="section04">
                <div className="mb-4 text-xl font-bold">
                  4. Recipients: To whom can we disclose your data?
                </div>
                <section className="mb-3">
                  The legal basis that allows us to process your data for each
                  of the purposes specified in section 2 above are:
                  <div className="mt-4 font-bold">
                    In relation to paragraph 2.I above (services requested)
                  </div>
                  <ol type="a" className="ml-4 mt-4 list-[upper-roman]">
                    <li className="mb-1">
                      To the advertisers that you have contacted to handle your
                      request.
                    </li>
                    <li className="mb-1">
                      Courts or Tribunals or Public Authorities to fulfil any
                      legal requirement.
                    </li>
                  </ol>
                  <div className="mt-4 font-bold">
                    In relation to paragraph 2.III above, (if you are a User,
                    Registered User or a Private Advertiser)
                  </div>
                  <ol type="a" className="ml-4 mt-4 list-[upper-roman]">
                    <li className="mb-1">
                      Third parties in the business sector, consultants and
                      other companies of {companyName} when you have accepted
                      this and for the purpose of sending you direct marketing
                      messages.
                    </li>
                  </ol>
                  <div className="mt-4 font-bold">
                    For the remaining purposes
                  </div>
                  <section className="mt-4">
                    Your data will not be disclosed to third parties unless we
                    need to fulfil any legal requirement.
                  </section>
                  <section className="mt-4">
                    To enable the publication of listings and the management of
                    business between brands belonging to the same group, your
                    personal data may be communicated to other services in the
                    group to which {companyName} belongs.
                  </section>
                  <section className="mt-4">
                    {companyName}&apos;s data processors may access your
                    personal data, in other words, {companyName}&apos;s service
                    providers that must access your data in order to perform
                    their duties, including technology service providers,
                    documentation tools and support tools in order to assess the
                    functioning of the Services and the use of certain functions
                    by the users.
                  </section>
                  <section className="mt-4">
                    You can request further information about the use of your
                    data by sending an email to the email address privacy(at)
                    {serviceWebsite} specifying the specific processing or use
                    for which you require information about the recipients.
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section05">
                <div className="mb-4 text-xl font-bold">
                  5. International data transfers: where can we send them?
                </div>
                <section className="mb-3">
                  We will as a rule, your personal data are stored in
                  computer/paper resources in countries within the European
                  Union; in case, for organisational reasons/business choices of{' '}
                  {companyName} or the relevant group, it becomes necessary to
                  transfer them to countries outside the European Union, the
                  measures provided for by European Regulation 2016/679 in
                  Paragraphs 44-49 will be adopted, as from time to time most
                  appropriate to the specific case of transfer your personal
                  data internationally for any of the purposes listed above.
                </section>
              </section>
              <section className="mt-16" id="section06">
                <div className="mb-4 text-xl font-bold">
                  6. Period for storing your data: how long will we process
                  them?
                </div>
                <section className="mb-3">
                  Depending on each processing purpose, the period for storing
                  your data may be:
                  <ol className="ml-4 mt-4 list-disc">
                    <li className="mb-1">
                      <span className="font-semibold">
                        During the whole contractual relationship
                      </span>
                      , in other words while you are using our services or while
                      we or the supplier is handling your requests (applicable
                      to paragraphs 2.I above).
                    </li>
                    <li className="mb-1">
                      <span className="font-semibold">
                        Until the query or claim has been settled
                      </span>{' '}
                      (paragraphs 2.II and 2.VI).
                    </li>
                    <li className="mb-1">
                      <span className="font-semibold">
                        Until you revoke your consent
                      </span>{' '}
                      (paragraphs 2.III and 2.V).
                    </li>
                    <li className="mb-1">
                      <span className="font-semibold">
                        If you are a representative or contact person of a
                        Professional Advertiser, during the contractual
                        relationship
                      </span>{' '}
                      with the company you represent or until you notify us that
                      you are no longer its contact person (paragraph 2.V).
                    </li>
                    <li className="mb-1">
                      <span className="font-semibold">
                        Until you request its cancellation
                      </span>{' '}
                      (applies to all purposes under section 2).
                    </li>
                  </ol>
                  <section className="mb-3 mt-6">
                    In addition, we hereby inform you that your data will remain
                    blocked after the end of the processing for which they are
                    intended and during the time-barring (prescription) periods
                    of obligations for each purpose stated in section 2.
                  </section>
                  <section className="mb-3">
                    If you are Private Advertiser (whether you have used the
                    free or the paid service), we hereby inform you that if you
                    request to delete your {serviceName} account, we will keep
                    your telephone number and email address in a blocked state
                    from the deactivation date of the last of your listings up
                    to the legal time-barring period. During this period, we
                    retain the right to carry out security checks to ensure the
                    correct working of our services.
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section07">
                <div className="mb-4 text-xl font-bold">
                  7. Required updated information
                </div>
                <section className="mb-3">
                  There are fields in the forms that are for mandatory
                  completion, without which we will be unable to render you the
                  services you request. When you click on the &quot;Ok, let’s
                  go&quot; button (or equivalent) included in the aforementioned
                  forms, you state that the information and the data that you
                  provide are correct and true.
                </section>
                <section className="mb-3">
                  In order for the information provided to always be up to date
                  and so that it does not contain any errors, please remember to
                  inform us as soon as possible of any changes and
                  rectifications of your personal data that occur by using the
                  following email address: privacy(at){serviceWebsite}. You can
                  also update your personal data through your private area on
                  the Services.
                </section>
              </section>
              <section className="mt-16" id="section08">
                <div className="mb-4 text-xl font-bold">
                  8. Rights of data subjects
                </div>
                <section className="mb-3">
                  <div>
                    We hereby inform you that you can exercise the following
                    rights:
                  </div>
                  <ol className="ml-6 mt-4 list-decimal">
                    <li className="mb-1">
                      The right to access your personal data to know which ones
                      are subject to processing and the data processing
                      operations carried out with them
                    </li>
                    <li className="mb-1">
                      The right to rectify any inaccurate personal data
                    </li>
                    <li className="mb-1">
                      The right to cancel your personal data, whenever this is
                      possible (for example, by a legal order)
                    </li>
                    <li className="mb-1">
                      The right to request the restriction of your personal data
                      processing when the accuracy, legality or need for the
                      data processing is in doubt, in such case, we can keep
                      them to file or defend any claims.
                    </li>
                    <li className="mb-1">
                      The right to oppose processing of your personal data, when
                      the legal basis that authorises us to process them in our
                      legitimate interest allows this. {companyName} will stop
                      processing your data unless it has a legitimate interest
                      or it is necessary to defend claims.
                    </li>
                    <li className="mb-1">
                      The right to the portability of your data, when the legal
                      basis that authorises us to process them is the existence
                      of a contractual relationship or your consent has been
                      granted.
                    </li>
                    <li className="mb-1">
                      The right to revoke the consent granted to {companyName}.
                    </li>
                  </ol>
                  <section className="mt-4">
                    You can exercise your rights at any time free of charge in
                    the following ways:
                    <ol className="ml-6 mt-4 list-decimal">
                      <li className="mb-1">
                        From the private area on the Services, you can
                        unsubscribe to the commercial messages previously
                        accepted. Through this section you can select and
                        customise the kind of messages you wish to receive from{' '}
                        {companyName}.
                      </li>
                      <li className="mb-1">
                        If you are unable to access the Services, you can send
                        an email to privacy(at){serviceWebsite} specifying the
                        right that you wish to exercise and your identifying
                        data.
                      </li>
                    </ol>
                  </section>
                  <section className="mt-4">
                    We also inform you that you are entitled to submit a claim
                    to the Italian authority &quot;Garante per la protezione dei
                    dati personali&quot; if you consider an infringement has
                    been committed of the data protection laws when processing
                    your personal data.
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section09">
                <div className="mb-4 text-xl font-bold">
                  9. Confidentiality and security
                </div>
                <section className="mt-4">
                  At {serviceName}, we make all reasonable efforts to keep the
                  personal information confidential that is processed through
                  the Services and if need be, other {companyName} tools. We
                  will apply strict security levels to protect the personal data
                  we process to avoid accidental loss and unauthorised access,
                  processing or disclosure, bearing in mind the state of the
                  art, the nature and risks to which the data are exposed.
                  However, we cannot be held responsible for your use of the
                  data (including the username and password) at {serviceName}{' '}
                  that is not through our Services.
                </section>
              </section>
              <section className="mt-16" id="section10">
                <div className="mb-4 text-xl font-bold">
                  10. Additional information about certain functions of the
                  Services
                </div>
                <section className="mt-8">
                  <div className="text-lg font-semibold">
                    Use of the message archive and internal chat messaging
                  </div>
                  <div className="mt-4">
                    For the purpose of improving your experience, {serviceName}{' '}
                    will make a message archive service (sent or received) and
                    internal messaging (chat) available to you on the Services
                    through which Users, Registered Users and Advertisers can
                    begin to make contact or continue their chats. By accepting
                    this Privacy Policy, you expressly consent to {companyName}{' '}
                    storing such chats; accessing them and using automatic
                    systems to check them to ensure fulfilment of the general
                    terms and conditions for using the service, developing and
                    improving our services and for the other purposes outlined
                    in this policy.
                  </div>
                  <div className="mt-4">
                    We reserve the right to block access to the messaging
                    service if, at our discretion, we consider that the chats
                    could contain malicious content, spam or imply any risk for
                    Users, Registered Users or Advertisers. Since {companyName}{' '}
                    provides various forms of contact, Users or Advertisers can
                    choose to directly contact you using the email address, your
                    telephone number provided or by other communication channels
                    not under the control of {companyName}.
                  </div>
                </section>
                <section className="mt-8">
                  <div className="text-lg font-semibold">
                    Registering a username and password
                  </div>
                  <div className="mt-4">
                    When signing up on the Services, you must specify a username
                    and password that will allow you to start a session at any
                    time as a &quot;Registered User&quot; and therefore manage
                    your relationship with {companyName}. The password can be
                    changed.
                  </div>
                  <div className="mt-4">
                    It is indispensable that you store and keep the password
                    secret at all times and that you do not provide it to any
                    third parties. It is advisable not to use the same password
                    for all your personal accounts so that, if any third party
                    accesses them without your consent, such person will not be
                    able to access the different accounts that you have on
                    different websites.
                  </div>
                </section>
                <section className="mt-8">
                  <div className="text-lg font-semibold">
                    Passively collected information
                  </div>
                  <div className="mt-4">
                    When rendering our Services we will use software and/or
                    services of third parties to passively, without your
                    knowledge, collect data, while you are browsing or
                    interacting with the content of our Services or that of the
                    subsidiaries or investee brands of {companyName}, as well as
                    information about the device you use.
                  </div>
                  <div className="mt-4">
                    The sole purpose of passively collecting this information is
                    to learn more about how you use and interact with the
                    Services in order to improve your experience while using our
                    services and detect possible errors in the implementation of
                    our Services. The information collected will include the
                    following, among others:
                    <ol className="ml-6 mt-4 list-disc">
                      <li className="mb-1">The IP address of your device</li>
                      <li className="mb-1">
                        The screen size and resolution of the device you use
                      </li>
                      <li className="mb-1">
                        Type of device (unique device identifier) and
                        information about the browser
                      </li>
                      <li className="mb-1">Geographic location</li>
                      <li className="mb-1">
                        Preferred language used on our Services
                      </li>
                      <li className="mb-1">Web pages visited</li>
                      <li className="mb-1">
                        Date and time of the visits to the Services
                      </li>
                    </ol>
                  </div>
                  <div className="mt-4">
                    For more information on passively collected information,
                    please review our{' '}
                    <a
                      data-test-id="cookie-policy-link"
                      href={toLocalizedRoute(ROUTES.COOKIE_POLICY_ROUTE)}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Cookies Policy
                    </a>
                    .
                  </div>
                </section>
              </section>
              <section className="mt-16" id="section11">
                <div className="mb-4 text-xl font-bold">
                  11. Types of data collected
                </div>
                <section className="mt-4">
                  We have collected the following categories of personal data
                  from or about consumers within the last 12 months:
                  <section>
                    <CollectedDataGrid />
                  </section>
                </section>
                <div className="mt-4">
                  For more information on passively collected information,
                  please review our{' '}
                  <a
                    data-test-id="cookie-policy-link"
                    href={toLocalizedRoute(ROUTES.COOKIE_POLICY_ROUTE)}
                    className="cursor-pointer text-sky-600 hover:text-sky-400"
                  >
                    Cookies Policy
                  </a>
                  .
                </div>
              </section>
              <section className="mt-16" id="section12">
                <div className="mb-4 text-xl font-bold">
                  12. Third party services involved in the processing of your
                  data
                </div>
                <section className="mt-4">
                  <ThirdPartyServicesGrid />
                </section>
              </section>
            </div>
          </section>
          <div className="mt-1">Thank you for reading to the end 🙏</div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
